<template>
  <div class="min-h-screen bg-zinc-100 text-zinc-900 dark:bg-[#181a1f] dark:text-zinc-100">
    <header class="sticky top-0 z-50 border-b border-gray-300 bg-primary-500 px-4 py-3 text-zinc-900 backdrop-blur dark:border-zinc-800 dark:bg-zinc-950/92 dark:text-zinc-100">
      <div class="mx-auto flex max-w-5xl items-center justify-between gap-3">
        <button
          type="button"
          class="flex min-w-0 items-center gap-3 text-left"
          :class="mustChangePassword ? 'cursor-default' : 'cursor-pointer'"
          @click="goDashboard"
        >
          <img
            src="/avenue44-logo.png"
            :alt="$t('label.logo')"
            class="hidden h-8 dark:block"
          >
          <img
            src="/logo-cropped.jpg"
            :alt="$t('label.logo')"
            class="h-8 dark:hidden"
          >
        </button>

        <div class="flex shrink-0 items-center gap-2">
          <button
            v-if="!mustChangePassword"
            type="button"
            class="hidden size-10 cursor-pointer items-center justify-center rounded-full border border-white/30 bg-white/18 text-white transition hover:bg-white/28 dark:border-zinc-800 dark:bg-zinc-900 dark:text-zinc-200 dark:hover:bg-zinc-800 sm:inline-flex"
            :aria-label="$t('label.search')"
            @click="$router.push({ name: 'EmployeeSearch' })"
          >
            <i class="far fa-search"></i>
          </button>
          <button
            v-if="!mustChangePassword"
            type="button"
            class="inline-flex size-10 cursor-pointer items-center justify-center rounded-full border border-white/30 bg-white/18 text-white transition hover:bg-white/28 dark:border-zinc-800 dark:bg-zinc-900 dark:text-zinc-200 dark:hover:bg-zinc-800"
            :aria-label="$t('menu.account')"
            @click="$router.push({ name: 'EmployeeAccount' })"
          >
            <i class="far fa-user"></i>
          </button>
          <button
            v-if="!mustChangePassword"
            type="button"
            class="hidden size-10 cursor-pointer items-center justify-center rounded-full border border-white/30 bg-white/18 text-white transition hover:bg-white/28 dark:border-zinc-800 dark:bg-zinc-900 dark:text-zinc-200 dark:hover:bg-zinc-800 sm:inline-flex"
            :aria-label="$t('menu.qr_scan')"
            @click="showQrScanner = true"
          >
            <i class="far fa-qrcode"></i>
          </button>
          <button
            type="button"
            class="size-10 cursor-pointer items-center justify-center rounded-full border border-white/30 bg-white/18 text-white transition hover:bg-white/28 dark:border-zinc-800 dark:bg-zinc-900 dark:text-zinc-200 dark:hover:bg-zinc-800"
            :class="mustChangePassword ? 'inline-flex' : 'hidden sm:inline-flex'"
            :aria-label="$t('label.logout')"
            @click="$router.replace('/logout')"
          >
            <i class="far fa-sign-out"></i>
          </button>
        </div>
      </div>
    </header>

    <main class="mx-auto min-h-[calc(100vh-64px)] w-full max-w-5xl px-4 pb-24 pt-4 sm:pb-6 sm:pt-6">
      <router-view />
    </main>

    <nav
      v-if="!mustChangePassword"
      class="fixed inset-x-0 bottom-0 z-40 border-t border-gray-300 bg-primary-500 px-4 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-2 backdrop-blur dark:border-zinc-800 dark:bg-zinc-950/95 sm:hidden"
    >
      <div class="mx-auto grid max-w-md grid-cols-2 gap-2">
        <button
          type="button"
          class="employee-bottom-button"
          :class="isDashboardRoute ? 'is-active' : ''"
          @click="goDashboard"
        >
          <i class="far fa-home"></i>
          <span>{{ $t('label.dashboard') }}</span>
        </button>
        <button
          type="button"
          class="employee-bottom-button"
          :class="showQrScanner ? 'is-active' : ''"
          @click="showQrScanner = true"
        >
          <i class="far fa-qrcode"></i>
          <span>{{ $t('menu.qr_scan') }}</span>
        </button>
      </div>
    </nav>

    <Drawer
      v-if="!mustChangePassword"
      v-model:visible="showQrScanner"
      position="bottom"
      class="employee-scanner-drawer !m-0 !h-[100dvh] !max-h-[100dvh] !w-full max-w-full border-0 p-0"
      :dismissable="true"
      :show-close-icon="false"
      modal
    >
      <template #container>
        <MobileQrScanner employee-mode @close="showQrScanner = false" />
      </template>
    </Drawer>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { Drawer } from 'primevue'
import MobileQrScanner from '@/components/MobileQrScanner.vue'

export default {
  name: 'EmployeeLayout',

  components: {
    Drawer,
    MobileQrScanner,
  },

  data() {
    return {
      showQrScanner: false,
    }
  },

  computed: {
    ...mapGetters(['user']),

    isDashboardRoute() {
      return this.$route.name === 'EmployeeDashboard'
    },

    mustChangePassword() {
      return this.user?.mustChangePassword === true
    },
  },

  methods: {
    goDashboard() {
      if (this.mustChangePassword) {
        return
      }

      if (this.$route.name !== 'EmployeeDashboard') {
        this.$router.push({ name: 'EmployeeDashboard' })
      }
    },
  },
}
</script>

<style scoped>
.employee-bottom-button {
  display: inline-flex;
  min-height: 3rem;
  cursor: pointer;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  border-radius: 0.875rem;
  color: rgb(255 255 255);
  font-size: 0.875rem;
  font-weight: 700;
  transition: background-color 150ms ease, color 150ms ease;
}

.employee-bottom-button.is-active,
.employee-bottom-button:hover {
  background: rgb(255 255 255 / 0.2);
  color: rgb(255 255 255);
}

.dark .employee-bottom-button {
  color: rgb(212 212 216);
}

.dark .employee-bottom-button.is-active,
.dark .employee-bottom-button:hover {
  background: rgb(20 184 166 / 0.18);
  color: rgb(94 234 212);
}

:deep(.employee-scanner-drawer.p-drawer-bottom) {
  border-top-left-radius: 1.25rem;
  border-top-right-radius: 1.25rem;
  overflow: hidden;
}

@media (min-width: 640px) {
  :deep(.employee-scanner-drawer.p-drawer-bottom) {
    border-radius: 0;
  }
}
</style>
